import React, { useState, useContext, useEffect } from "react";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import RealTimeClock from "./RealTimeClock";
import { useNavigate } from "react-router";
import ConfirmModal from "../../components/ConfirmModal";
import GeneratePin from "./GeneratePin";
import { CustomerContext } from "../CashierHomepage";
import { Badge } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalculateIcon from "@mui/icons-material/Calculate";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import PrintIcon from "@mui/icons-material/Print";
import QueuedOrdersModal from "./QueuedOrdersModal";
import PrinterSettingsModal from "./PrinterSettingsModal";
import Calculator from "../../utility/Calculator";
import useReceiptTemplate from "../utils/useReceiptTemplate";
import { getLastReceipt, printReceipt } from "../utils/printReceipt";
import { getSavedPrinter, hasPrintBridge } from "../utils/printBridge";

function ProductsHeader() {
  const {
    setOpenDraft,
    openMessage,
    barcodeScan,
    openGeneratePassword,
    setOpenMessage,
    setOpenGeneratePassword,
    setBarcodeScan,
    totalUnreadMessages,
    currency,
  } = useContext(CustomerContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(getLastReceipt);
  const [showPrinters, setShowPrinters] = useState(false);
  const [savedPrinter, setSavedPrinter] = useState(getSavedPrinter);
  const template = useReceiptTemplate();
  // Inside the desktop app with no printer chosen yet: flag the icon.
  const needsPrinter = hasPrintBridge() && !savedPrinter;

  useEffect(() => {
    const sync = () => setSavedPrinter(getSavedPrinter());
    window.addEventListener("receipt-printer-changed", sync);
    return () => window.removeEventListener("receipt-printer-changed", sync);
  }, []);

  // Payment saves the receipt it prints; keep the icon in sync with that.
  useEffect(() => {
    const sync = () => setLastReceipt(getLastReceipt());
    window.addEventListener("last-receipt-changed", sync);
    return () => window.removeEventListener("last-receipt-changed", sync);
  }, []);

  const handleReprint = () => {
    if (lastReceipt) {
      printReceipt({ receipt: lastReceipt, template, currency, kind: "reprint" });
    }
  };

  const handleChat = () => {
    setOpenMessage(!openMessage);
    setOpenDraft(false);
    setOpenGeneratePassword(false);
    setBarcodeScan(false);
  };

  const handleCalculator = () => {
    setBarcodeScan(!barcodeScan);
    setOpenMessage(false);
    setOpenDraft(false);
    setOpenGeneratePassword(false);
  };

  const handleGeneratePasscode = () => {
    setOpenMessage(false);
    setOpenDraft(false);
    setOpenGeneratePassword(!openGeneratePassword);
    setBarcodeScan(false);
  };

  const handleQuit = () => {
    setIsModalOpen(true); // Open the confirmation modal
  };

  const handleCloseModal = () => {
    setIsModalOpen(false); // Close the modal
  };

  const navigate = new useNavigate();
  const handleConfirmQuit = () => {
    sessionStorage.clear();

    navigate("/login");
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <header className="flex flex-row justify-between bg-black h-12 px-3 items-center">
      {/* <img src={logo} className="w-20 " alt="logo of the company" /> */}
      <h2 className="font-bold text-white text-2xl">01POS</h2>
      <RealTimeClock />
      <div className="flex flex-row justify-between gap-3 items-center">
        <Badge
          color="secondary"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "8px", // Reduce font size
              width: "12px", // Reduce the size of the badge circle
              height: "16px", // Reduce the size of the badge circle
            },
          }}
          badgeContent={totalUnreadMessages}
        >
          <ChatBubbleOutlineIcon
            className="text-white cursor-pointer hover:text-blue-500"
            onClick={handleChat}
          />
        </Badge>
        <GeneratePin handleGeneratePasscode={handleGeneratePasscode} />

        <CalculateIcon
          className="text-white cursor-pointer "
          onClick={handleCalculator}
        />
        <ReceiptLongIcon
          onClick={handleReprint}
          titleAccess={
            lastReceipt ? "Reprint last receipt" : "No receipt available"
          }
          className={
            lastReceipt
              ? "text-white cursor-pointer hover:text-blue-500"
              : "text-gray-600 cursor-not-allowed"
          }
        />
        <PrintIcon
          onClick={() => setShowPrinters(true)}
          titleAccess={
            needsPrinter
              ? "Printer settings: no receipt printer chosen"
              : `Printer settings${savedPrinter ? ` (${savedPrinter})` : ""}`
          }
          className={
            needsPrinter
              ? "text-amber-400 cursor-pointer hover:text-blue-500"
              : "text-white cursor-pointer hover:text-blue-500"
          }
        />
        <HourglassTopIcon
          onClick={() => setShowQueue(true)}
          titleAccess="Queue"
          className="text-white cursor-pointer hover:text-blue-500"
        />
        <RefreshIcon
          onClick={handleRefresh}
          className="text-white cursor-pointer hover:text-green-500"
        />
        <ExitToAppIcon
          onClick={handleQuit}
          className="text-white cursor-pointer hover:text-red-500"
        />
      </div>
      {showQueue && (
        <QueuedOrdersModal
          onClose={() => setShowQueue(false)}
          currency={currency}
        />
      )}
      {showPrinters && (
        <PrinterSettingsModal
          onClose={() => setShowPrinters(false)}
          template={template}
          currency={currency}
        />
      )}
      {barcodeScan && <Calculator handleClose={setBarcodeScan} />}
      <ConfirmModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmQuit}
        text1="Confirm Quit"
        text2="Are you sure you want to quit the app?"
        close={"No"}
        accept={"Yes"}
      />
    </header>
  );
}

export default ProductsHeader;
